import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  User,
  Mail,
  Lock,
  AlertCircle,
  Building2,
  Users,
  Ban as Bank,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Phone
} from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

interface RegisterFormData {
  email: string;
  password: string;
  confirmPassword: string;
  fullName: string;
  phoneNumber: string;
  accountType: 'personal' | 'business';
  companyName?: string;
  companySize?: string;
  role?: string;
}

type Step = {
  title: string;
  subtitle: string;
};

const steps: Step[] = [
  {
    title: "Let's get you in",
    subtitle: ''
  },
  {
    title: 'Tell us about yourself',
    subtitle: 'Help us personalize your experience'
  },
  {
    title: 'Almost there!',
    subtitle: 'Optional: Set up your organization'
  },
  {
    title: 'Ready to go!',
    subtitle: 'Your account is ready to use'
  }
];

const Register: React.FC = () => {
  const { register: registerUser, checkEmailExists } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [countryCode, setCountryCode] = useState('+1');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
    trigger,
    setError: setFormError,
    getValues
  } = useForm<RegisterFormData>({
    mode: 'onChange',
    defaultValues: {
      accountType: 'personal'
    }
  });

  const accountType = watch('accountType');
  const password = watch('password');

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    setError(null);
    try {
      await registerUser(data.fullName, data.email, data.password);
      toast.success('Registration successful');
      navigate('/login');
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.');
      toast.error('Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(0, prev - 1));
  };

  const checkEmail = useCallback(
    async (email: string) => {
      if (!email || !emailTouched) return;

      setIsCheckingEmail(true);
      try {
        const exists = await checkEmailExists(email);
        setEmailExists(exists);
        if (exists) {
          setFormError('email', {
            type: 'manual',
            message: 'This email is already registered'
          });
        }
      } catch (err) {
        console.error('Error checking email:', err);
      } finally {
        setIsCheckingEmail(false);
      }
    },
    [checkEmailExists, emailTouched, setFormError]
  );

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailTouched(true);
    checkEmail(e.target.value);
  };

  return (
    <div className="min-h-screen flex flex-col justify-center items-center bg-gray-50">
      <div className="w-full max-w-md bg-white rounded-xl shadow-xl p-8 border border-gray-100">
        <div className="flex flex-col items-center">
          <span className="text-3xl font-bold text-blue-700 mb-2">CashBitAi</span>
          <h2 className="text-xl font-semibold text-gray-900 mb-6 text-center">
            {steps[currentStep].title}
          </h2>
        </div>
        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <Input
            label="Email address"
            type="email"
            autoComplete="email"
            placeholder="Enter your email"
            leftIcon={<Mail className="h-5 w-5 text-gray-400" />}
            error={errors.email?.message}
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address'
              },
              onChange: handleEmailChange
            })}
            className="mb-2"
          />
          <div className="relative mb-2">
            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Create a password"
              leftIcon={<Lock className="h-5 w-5 text-gray-400" />}
              error={errors.password?.message}
              {...register('password', {
                required: 'Password is required',
                minLength: {
                  value: 8,
                  message: 'Password must be at least 8 characters'
                },
                pattern: {
                  value: /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z\d]+$/,
                  message: 'Password must be alphanumeric (contain both letters and numbers)'
                }
              })}
              className="pr-10"
            />
            <button
              type="button"
              className="absolute right-3 top-7 text-green-700 text-xs flex items-center"
              onClick={() => setShowPassword((v) => !v)}
              tabIndex={-1}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          <div className="relative mb-2">
            <Input
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Confirm your password"
              leftIcon={<Lock className="h-5 w-5 text-gray-400" />}
              error={errors.confirmPassword?.message}
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (value) => value === password || 'The passwords do not match'
              })}
              className="pr-10"
            />
            <button
              type="button"
              className="absolute right-3 top-7 text-green-700 text-xs flex items-center"
              onClick={() => setShowConfirmPassword((v) => !v)}
              tabIndex={-1}
            >
              {showConfirmPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          <div className="mb-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
            <div className="border border-gray-300 rounded-md px-3 py-2 w-full bg-gray-50 text-gray-400 select-none">
              +1 (optional)
            </div>
          </div>
          <Input
            label="Full Name"
            autoComplete="name"
            placeholder="Enter your full name"
            leftIcon={<User className="h-5 w-5 text-gray-400" />}
            error={errors.fullName?.message}
            {...register('fullName', {
              required: 'Full name is required',
              minLength: {
                value: 2,
                message: 'Name must be at least 2 characters'
              }
            })}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
            <div className="grid grid-cols-2 gap-4">
              <label
                className={`relative flex items-center justify-center p-4 rounded-lg border-2 cursor-pointer ${accountType === 'personal' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
              >
                <input
                  type="radio"
                  className="sr-only"
                  value="personal"
                  {...register('accountType')}
                />
                <div className="text-center">Personal</div>
              </label>
              <label
                className={`relative flex items-center justify-center p-4 rounded-lg border-2 cursor-pointer ${accountType === 'business' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
              >
                <input
                  type="radio"
                  className="sr-only"
                  value="business"
                  {...register('accountType')}
                />
                <div className="text-center">Business</div>
              </label>
            </div>
          </div>
          <Button type="submit" isLoading={isLoading} fullWidth>
            Create an CashBitAi Account
          </Button>
        </form>
        {error && (
          <div className="mt-4 bg-red-50 p-4 rounded-lg flex items-start">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        <p className="text-xs text-gray-500 mt-4 text-center">
          By selecting Create an{' '}
          <a href="#" className="text-blue-600 underline">
            CashBitAi Account
          </a>
          , you agree to our{' '}
          <a href="#" className="text-blue-600 underline">
            Terms
          </a>
          . Our{' '}
          <a href="#" className="text-blue-600 underline">
            Privacy Policy
          </a>{' '}
          applies to your personal data.
        </p>
        <div className="mt-4 text-center text-sm">
          Already have an account?{' '}
          <Link to="/login" className="text-blue-700 underline">
            Sign in
          </Link>
        </div>
        <div className="mt-4 text-center">
          <span className="text-[10px] text-gray-400">
            Invisible reCAPTCHA by Google{' '}
            <a href="#" className="underline">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href="#" className="underline">
              Terms of Use
            </a>
            .
          </span>
        </div>
      </div>
    </div>
  );
};

export default Register;